import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createMerchantAsset,
  deleteMerchantAsset,
  listMerchantAssets,
  updateMerchantAsset,
} from '@/lib/api/merchant-assets';
import type {
  AssetType,
  CreateMerchantAssetInput,
  UpdateMerchantAssetInput,
} from '@/types/merchant-asset';

/**
 * Query keys scoped by merchant — assets are merchant-scoped, so every key carries
 * the merchantId. Invalidating `lists(merchantId)` refreshes the library after a
 * create/update/delete.
 */
export const merchantAssetKeys = {
  all: ['merchant-assets'] as const,
  lists: (merchantId: string) => [...merchantAssetKeys.all, 'list', merchantId] as const,
  list: (merchantId: string, type?: AssetType) =>
    [...merchantAssetKeys.lists(merchantId), type ?? 'all'] as const,
};

export function useMerchantAssets(merchantId: string | undefined, type?: AssetType) {
  return useQuery({
    queryKey: merchantAssetKeys.list(merchantId ?? '', type),
    queryFn: () => listMerchantAssets(merchantId!, type),
    enabled: !!merchantId,
    staleTime: 30_000,
  });
}

export function useCreateMerchantAsset(merchantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMerchantAssetInput) => createMerchantAsset(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantAssetKeys.lists(merchantId) });
    },
  });
}

export function useUpdateMerchantAsset(merchantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, input }: { assetId: string; input: UpdateMerchantAssetInput }) =>
      updateMerchantAsset(assetId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantAssetKeys.lists(merchantId) });
    },
  });
}

export function useDeleteMerchantAsset(merchantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) => deleteMerchantAsset(assetId, merchantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantAssetKeys.lists(merchantId) });
    },
  });
}
